import { Checkboxes, Fieldset, Label, TextInput } from "nhsuk-react-components"
import { useContext, useState } from "react"
import { REQUIRED_ERROR_DEFINITION_URL } from "../../constants/customExtensionUrls"
import { QuestionnaireContext } from "../../context/QuestionnaireContext"
import {
  getChoiceAnswerFromResponse,
  getChoiceOptions,
  getResponseForQuestion,
  type ChoiceAnswer,
} from "../../fhirUtils/questionnaireUtils"
import type { QuestionProps } from "./Question"
import { QuestionnaireError } from "./QuestionnaireError"

/**
 * Multiple choice question using Checkboxes.
 */
export function MultipleChoiceQuestion({ question, saveResponses, topLevel }: QuestionProps): JSX.Element {
  // Questionnaire Context
  const questionnaireContext = useContext(QuestionnaireContext)
  if (!questionnaireContext) {
    throw new Error("ChoiceQuestion must be used within a QuestionnaireProvider")
  }
  const { responses, errors } = questionnaireContext

  const choiceOptions = getChoiceOptions(question)
  const response = getResponseForQuestion(question, responses)
  const [answer, setAnswer] = useState<ChoiceAnswer>(getChoiceAnswerFromResponse(choiceOptions, response))

  const { choices, other } = answer

  const updateAnswer = (updatedChoices: string[], otherValue: string): void => {
    const choicesToSave = otherValue.trim().length > 0 ? [...updatedChoices, otherValue.trim()] : updatedChoices
    setAnswer({ choices: updatedChoices, other: otherValue })
    saveResponses({
      linkId: question.linkId,
      text: question.text,
      answer: choicesToSave.map((valueString) => {
        return { valueString }
      }),
    })
  }

  const updateChoices = (updatedChoices: string[]): void => {
    updateAnswer(updatedChoices, other)
  }

  const updateOtherChoice = (otherChoice: string): void => {
    updateAnswer(choices, otherChoice)
  }

  const showRequiredError = !!errors[question.linkId]?.required

  const requiredErrorMessage = showRequiredError
    ? (question.extension?.find((ext) => ext.url === REQUIRED_ERROR_DEFINITION_URL)?.valueMarkdown ??
      "This field is required.")
    : null

  return (
    <form onSubmit={(e) => e.preventDefault()}>
      {showRequiredError && !choices.length && (
        <QuestionnaireError errorMessage={requiredErrorMessage} formElementId={question.linkId} />
      )}
      <Fieldset>
        {topLevel ? <Fieldset.Legend isPageHeading>{question.text}</Fieldset.Legend> : <Label>{question.text}</Label>}
        <Checkboxes id={question.linkId} error={showRequiredError && !choices.length}>
          {question.option?.map((option) => {
            const optionValue = option.valueString ?? ""
            const isSelectedChoice = choices.includes(optionValue)

            const onClick = (): void => {
              if (isSelectedChoice) {
                updateChoices(choices.filter((choiceText) => choiceText !== optionValue))
              } else {
                updateChoices([...choices, optionValue])
              }
            }
            return (
              <Checkboxes.Box value={optionValue} checked={isSelectedChoice} onClick={onClick} key={optionValue}>
                {optionValue}
              </Checkboxes.Box>
            )
          })}
          {question.type === "open-choice" && (
            <TextInput
              id="other"
              name="other"
              value={other}
              label="Other"
              onChange={(event: React.FormEvent<HTMLInputElement>) => {
                updateOtherChoice(event.currentTarget.value)
              }}
            />
          )}
        </Checkboxes>
      </Fieldset>
    </form>
  )
}
